import React, { useState, useEffect } from 'react';
import Button from '@atlaskit/button';
import CustomFilterCheckbox from './custom-filter-checkbox';
import './custom-filter-checkbox-wrapper.scss';

interface filterField {
  // iterableValues shows exact list of filter fields to choose from
  iterableValues: Array<{ key: string; value: string }>;
  // queryKey is one of defined graphql variables
  queryKey: string;
  // filterItem identifies whether filter is for Products, Payment Method, Status etc
  filterItem: string;
  // defaultSelectedFields is used to preselect the filter fields
  defaultSelectedFields?: string[];
}

export interface CustomFilterCheckboxWrapperProps {
  filterFields: Array<filterField>;
  handleFilterUpdate: (queryFilters: Array<filter>) => void;
  shouldHideClearFilterButton?: boolean;
  // Reset is used to clear all the filters from parent
  resetFromParent?: number;
  version?: number;
}

interface filter {
  key: string;
  value: string;
}

export const CustomFilterCheckboxWrapper: React.FC<CustomFilterCheckboxWrapperProps> = ({
  filterFields,
  handleFilterUpdate,
  shouldHideClearFilterButton = false,
  resetFromParent,
  version,
}) => {
  const [currentFilters, setCurrentFilters] = useState<Array<filter>>([]);
  const [clearSelected, setClearSelected] = useState<boolean>(false);

  const updateQueryFilter = (selectedFilters: []) => {
    setCurrentFilters(selectedFilters);
    handleFilterUpdate(selectedFilters);
  };

  const resetFilters = () => {
    setClearSelected(!clearSelected);
    setCurrentFilters([]);
    handleFilterUpdate([]);
  };

  useEffect(() => {
    if (version) return;
    setClearSelected(!clearSelected);
    setCurrentFilters([]);
  }, [resetFromParent, version]); // Adding clearSelected in the dependency array will cause infinite loop

  useEffect(() => {
    if (!version) return;

    if (resetFromParent && !clearSelected) {
      setClearSelected(true);
    } else if (!resetFromParent && clearSelected) {
      setClearSelected(false);
    }
  }, [resetFromParent, clearSelected, version]);

  return (
    <div className="custom-filter-checkbox-wrapper" data-testid="custom-filter-checkbox-wrapper">
      {filterFields.map((item: filterField, index: number) => {
        return (
          <CustomFilterCheckbox
            key={index}
            filterField={item.filterItem}
            queryKey={item.queryKey}
            filterableItems={item.iterableValues}
            updateQueryFilter={updateQueryFilter}
            shouldClearSelectedField={clearSelected}
            defaultSelectedFields={item.defaultSelectedFields}
            version={version}
          />
        );
      })}
      {!shouldHideClearFilterButton && currentFilters.length > 0 && (
        <Button appearance="link" testId="filter-reset-link" onClick={resetFilters}>
          Clear all filters
        </Button>
      )}
    </div>
  );
};
